/* MAŠINKA theme — vanilla JS, no dependencies */
(() => {
  'use strict';

  const T = window.theme || { routes: {}, strings: {} };
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
  const debounce = (fn, ms = 300) => {
    let t;
    return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); };
  };

  /* ---------------- Header height var ---------------- */
  const setHeaderHeight = () => {
    const h = $('sticky-header');
    if (h) document.documentElement.style.setProperty('--header-h', `${h.offsetHeight}px`);
  };
  window.addEventListener('resize', debounce(setHeaderHeight, 100));
  document.addEventListener('DOMContentLoaded', setHeaderHeight);

  /* ---------------- Dialogs (drawers, modals) ---------------- */
  const openDialog = (dialog) => {
    if (!dialog || dialog.open) return;
    $$('dialog[open]').forEach((d) => { if (d !== dialog) closeDialog(d, true); });
    dialog.showModal();
    document.documentElement.style.overflow = 'hidden';
  };
  const closeDialog = (dialog, instant = false) => {
    if (!dialog || !dialog.open) return;
    const done = () => {
      dialog.classList.remove('is-closing');
      dialog.close();
      if (!$('dialog[open]')) document.documentElement.style.overflow = '';
    };
    if (instant || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return done();
    dialog.classList.add('is-closing');
    setTimeout(done, 180);
  };
  window.MasinkaDialogs = { open: openDialog, close: closeDialog };

  // Only the theme's own dialogs: apps (e.g. Releasit COD form) also use <dialog>, data-open and data-close.
  const OWN_DIALOG = 'dialog.drawer, dialog.modal';
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open]');
    const target = opener && document.getElementById(opener.dataset.open);
    if (target && target.matches(OWN_DIALOG)) {
      e.preventDefault();
      openDialog(target);
      if (target.id === 'SearchDialog') setTimeout(() => $('#SearchDialogInput')?.focus(), 50);
      return;
    }
    const closer = e.target.closest('[data-close]');
    const closerDialog = closer && closer.closest(OWN_DIALOG);
    if (closerDialog) { closeDialog(closerDialog); return; }
    if (e.target.matches(OWN_DIALOG) && e.target.open) closeDialog(e.target); // backdrop click
  });
  document.addEventListener('cancel', (e) => {
    if (e.target.matches && e.target.matches(OWN_DIALOG)) { e.preventDefault(); closeDialog(e.target); }
  }, true);

  /* ---------------- Toast ---------------- */
  let toastTimer;
  const toast = (msg) => {
    const el = $('[data-toast]');
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { el.hidden = true; }, 3000);
  };

  /* ---------------- Sticky header ---------------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.lastY = window.scrollY;
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
      // Mega menu: open on hover (desktop), close others
      $$('details[data-mega]', this).forEach((d) => {
        d.addEventListener('mouseenter', () => { if (window.matchMedia('(hover: hover)').matches) d.open = true; });
        d.addEventListener('mouseleave', () => { if (window.matchMedia('(hover: hover)').matches) d.open = false; });
        d.addEventListener('toggle', () => {
          if (d.open) $$('details[data-mega]', this).forEach((o) => { if (o !== d) o.open = false; });
        });
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') $$('details[data-mega][open]', this).forEach((d) => { d.open = false; });
      });
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) $$('details[data-mega][open]', this).forEach((d) => { d.open = false; });
      });
    }
    onScroll() {
      const y = window.scrollY;
      this.classList.toggle('is-scrolled', y > 20);
      const megaOpen = $('details[data-mega][open]', this);
      if (y > 300 && y > this.lastY + 4 && !megaOpen) this.classList.add('is-hidden');
      else if (y < this.lastY - 4 || y < 300) this.classList.remove('is-hidden');
      this.lastY = y;
    }
  }
  customElements.define('sticky-header', StickyHeader);

  /* ---------------- Announcement rotator ---------------- */
  $$('[data-rotator]').forEach((track) => {
    const items = $$('.announcement__item', track);
    if (items.length < 2) return;
    let i = 0;
    const ms = (parseInt(track.dataset.interval, 10) || 5) * 1000;
    setInterval(() => {
      const cur = items[i];
      i = (i + 1) % items.length;
      cur.classList.remove('is-active');
      cur.classList.add('is-leaving');
      setTimeout(() => cur.classList.remove('is-leaving'), 450);
      items[i].classList.add('is-active');
    }, ms);
  });

  /* ---------------- Localization auto-submit ---------------- */
  document.addEventListener('change', (e) => {
    if (e.target.matches('[data-auto-submit]')) e.target.form.submit();
  });

  /* ---------------- Hero slider ---------------- */
  class HeroSlider extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-track]', this);
      this.slides = $$('[data-slide]', this);
      this.dots = $$('[data-dot]', this);
      if (this.slides.length < 2) return;
      this.index = 0;
      this.interval = (parseInt(this.dataset.interval, 10) || 6) * 1000;
      this.style.setProperty('--hero-interval', `${this.interval / 1000}s`);
      this.dots.forEach((dot) => dot.addEventListener('click', () => { this.go(+dot.dataset.dot); this.restart(); }));
      this.track.addEventListener('scroll', debounce(() => {
        const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
        if (i !== this.index) { this.index = i; this.updateDots(); this.restart(); }
      }, 80), { passive: true });
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (this.dataset.autoplay === 'true' && !reduce) {
        this.start();
        this.addEventListener('pointerenter', () => this.stop());
        this.addEventListener('pointerleave', () => this.start());
        document.addEventListener('visibilitychange', () => (document.hidden ? this.stop() : this.start()));
      }
    }
    go(i) {
      this.index = (i + this.slides.length) % this.slides.length;
      this.track.scrollTo({ left: this.index * this.track.clientWidth, behavior: 'smooth' });
      this.updateDots();
    }
    updateDots() {
      this.dots.forEach((d, n) => {
        d.classList.remove('is-active');
        if (n === this.index) { void d.offsetWidth; d.classList.add('is-active'); }
      });
    }
    start() { this.stop(); if (this.dataset.autoplay === 'true') this.timer = setInterval(() => this.go(this.index + 1), this.interval); }
    stop() { clearInterval(this.timer); }
    restart() { if (this.timer) this.start(); }
  }
  customElements.define('hero-slider', HeroSlider);

  /* ---------------- Card swatches swap image ---------------- */
  document.addEventListener('click', (e) => {
    const sw = e.target.closest('.card__swatch');
    if (!sw) return;
    const card = sw.closest('[data-card]');
    const img = card && $('[data-card-img]', card);
    $$('.card__swatch', card).forEach((s) => s.classList.toggle('is-active', s === sw));
    if (img && sw.dataset.swatchImg) {
      img.srcset = sw.dataset.swatchSrcset || '';
      img.src = sw.dataset.swatchImg;
    }
  });
  document.addEventListener('mouseover', (e) => {
    const sw = e.target.closest && e.target.closest('.card__swatch');
    if (sw && window.matchMedia('(hover: hover)').matches) sw.click();
  });

  /* ---------------- Cart ---------------- */
  const Cart = {
    drawer: () => document.getElementById('CartDrawer'),
    sectionsParam: () => 'cart-drawer',
    render(sections) {
      if (!sections || !sections['cart-drawer']) return;
      const doc = parse(sections['cart-drawer']);
      const fresh = $('[data-cart-drawer]', doc);
      const cur = $('[data-cart-drawer]');
      // Keep third-party COD button containers (Releasit renders into #cart-wrapper once per page load)
      const appWrapper = (cur && $('#cart-wrapper', cur)) || Cart.appWrapper;
      if (appWrapper) Cart.appWrapper = appWrapper;
      if (fresh && cur) cur.replaceWith(fresh);
      if (fresh && appWrapper) {
        const btn = $('#CartDrawer-Checkout', fresh);
        if (btn && btn.parentNode) {
          const form = btn.parentNode;
          form.style.display = 'flex';
          form.style.flexDirection = 'column';
          btn.after(appWrapper);
        }
      }
      const count = fresh ? parseInt(fresh.dataset.count, 10) : 0;
      $$('[data-cart-count]').forEach((el) => { el.textContent = count; el.hidden = count === 0; });
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { count } }));
    },
    async add(formData) {
      formData.append('sections', this.sectionsParam());
      formData.append('sections_url', window.location.pathname);
      const res = await fetch(`${T.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || T.strings.error);
      return data;
    },
    async change(line, quantity) {
      const res = await fetch(`${T.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: this.sectionsParam(), sections_url: window.location.pathname }),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || T.strings.error);
      return data;
    },
    open() { openDialog(this.drawer()); loadCartRecs(); },
  };

  // Header cart link opens drawer
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-cart-toggle]');
    if (!t || T.cartType !== 'drawer' || document.body.classList.contains('template-cart')) return;
    e.preventDefault();
    Cart.open();
  });

  // Quantity change / remove (drawer + page)
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-qty-change]');
    if (!btn) return;
    const line = btn.closest('[data-line]');
    const wrap = btn.closest('[data-cart-items]');
    if (!line) return;
    wrap && wrap.classList.add('is-loading');
    try {
      const data = await Cart.change(+line.dataset.line, +btn.dataset.qtyChange);
      if (document.body.classList.contains('template-cart') && !btn.closest('#CartDrawer')) {
        window.location.reload();
        return;
      }
      Cart.render(data.sections);
      loadCartRecs();
    } catch (err) {
      toast(err.message);
      wrap && wrap.classList.remove('is-loading');
    }
  });

  // Any add-to-cart form (product page, quick add, cards, recommendations)
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!form.matches('form[action*="/cart/add"]')) return;
    e.preventDefault();
    const submitters = $$(`[type="submit"][form="${form.id}"], [type="submit"]`, document).filter((b) => b.form === form);
    const errorEl = $('[data-form-error]', form);
    if (errorEl) errorEl.hidden = true;
    submitters.forEach((b) => b.setAttribute('aria-busy', 'true'));
    try {
      const data = await Cart.add(new FormData(form));
      if (T.cartType === 'page') { window.location.href = T.routes.cart; return; }
      Cart.render(data.sections);
      const qa = document.getElementById('QuickAdd');
      if (qa && qa.open) closeDialog(qa, true);
      Cart.open();
    } catch (err) {
      if (errorEl) { errorEl.textContent = err.message; errorEl.hidden = false; } else toast(err.message);
    } finally {
      submitters.forEach((b) => b.removeAttribute('aria-busy'));
    }
  });

  /* ---------------- Cart recommendations ---------------- */
  const loadCartRecs = async () => {
    const el = $('#CartDrawer cart-recommendations');
    if (!el || el.dataset.loaded) return;
    el.dataset.loaded = '1';
    const exclude = (el.dataset.exclude || '').split(',');
    const fetchHtml = async (url) => (await fetch(url)).text();
    try {
      let html = await fetchHtml(el.dataset.url);
      let doc = parse(html);
      if (!$('.cart-recs__item', doc)) { html = await fetchHtml(el.dataset.fallbackUrl); doc = parse(html); }
      $$('.cart-recs__item', doc).forEach((item) => { if (exclude.includes(item.dataset.productId)) item.remove(); });
      const inner = $('.cart-recs__inner', doc);
      if (inner && $('.cart-recs__item', inner)) el.replaceChildren(inner);
    } catch (_) { /* silent */ }
  };
  customElements.define('cart-recommendations', class extends HTMLElement {});

  /* ---------------- Quick add ---------------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    const dialog = document.getElementById('QuickAdd');
    const body = $('[data-quick-add-content]', dialog);
    body.innerHTML = '';
    openDialog(dialog);
    try {
      const url = btn.dataset.quickAdd;
      const res = await fetch(`${url}${url.includes('?') ? '&' : '?'}section_id=quick-add`);
      const doc = parse(await res.text());
      const content = $('.qa', doc);
      if (content) body.replaceChildren(content);
    } catch (err) {
      body.textContent = T.strings.error;
    }
  });

  /* ---------------- Product page: variants ---------------- */
  class ProductPage extends HTMLElement {
    connectedCallback() {
      const json = $('script[data-variants]', this);
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.colorIndex = parseInt(this.dataset.colorIndex, 10);
      const tpl = $('script[data-low-stock-template]', this);
      this.lowStockTpl = tpl ? JSON.parse(tpl.textContent) : '';
      this.picker = $('variant-picker', this);
      if (this.picker) this.picker.addEventListener('change', () => this.onChange());
      if (this.picker) this.updateAvailability(this.currentOptions());
      this.initSticky();
    }
    currentOptions() {
      return $$('fieldset[data-option-index]', this.picker).map((fs) => {
        const c = $('input:checked', fs);
        return c ? c.value : null;
      });
    }
    findVariant(opts) {
      return this.variants.find((v) => v.options.every((o, i) => o === opts[i]));
    }
    onChange() {
      let opts = this.currentOptions();
      let variant = this.findVariant(opts);
      // If combination doesn't exist, pick closest available keeping the changed color/first option
      if (!variant) {
        variant = this.variants.find((v) => v.available && v.options[0] === opts[0]) || this.variants.find((v) => v.options[0] === opts[0]);
        if (variant) {
          variant.options.forEach((val, i) => {
            const fs = $(`fieldset[data-option-index="${i}"]`, this.picker);
            const input = fs && $$('input', fs).find((inp) => inp.value === val);
            if (input) input.checked = true;
          });
          opts = variant.options;
        }
      }
      $$('fieldset[data-option-index]', this.picker).forEach((fs, i) => {
        const sel = $('[data-selected]', fs);
        if (sel) sel.textContent = opts[i] || '';
      });
      this.updateAvailability(opts);
      if (variant) this.applyVariant(variant);
    }
    updateAvailability(opts) {
      $$('fieldset[data-option-index]', this.picker).forEach((fs, idx) => {
        $$('input', fs).forEach((input) => {
          const test = opts.slice();
          test[idx] = input.value;
          const ok = this.variants.some((v) => v.available && v.options.every((o, i) => o === test[i]));
          const label = input.nextElementSibling;
          if (label) label.classList.toggle('is-unavailable', !ok);
        });
      });
    }
    applyVariant(v) {
      $$('[data-variant-id]', this).forEach((i) => { i.value = v.id; });
      $$('[data-price]', this).forEach((el) => { el.innerHTML = v.price; });
      $$('[data-add-btn]', this).forEach((b) => {
        b.disabled = !v.available;
        const l = $('[data-add-label]', b);
        if (l) l.textContent = v.available ? T.strings.addToCart : T.strings.soldOut;
      });
      const sv = $('[data-sticky-variant]', this);
      if (sv) sv.textContent = v.title;
      const low = $('[data-low-stock]', this);
      if (low) {
        const max = parseInt(low.dataset.threshold, 10) || 0;
        const show = v.qty !== null && v.qty > 0 && v.qty <= max;
        low.hidden = !show;
        const txt = $('[data-low-stock-text]', low);
        if (show && txt && this.lowStockTpl) txt.textContent = this.lowStockTpl.replace('__COUNT__', v.qty);
      }
      // Quick add image
      const qaImg = $('[data-qa-img]', this);
      if (qaImg && v.img) { qaImg.srcset = ''; qaImg.src = v.img; }
      // Gallery
      const gallery = $('media-gallery', this);
      if (gallery && this.colorIndex > -1) gallery.filterByColor(this.keyOf(v.options[this.colorIndex]), v.media);
      else if (gallery && v.media) gallery.showMedia(v.media);
      if (!this.hasAttribute('data-no-history')) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url.toString());
      }
    }
    keyOf(name) {
      return (name || '')
        .toLowerCase()
        .replace(/č|ć/g, 'c').replace(/š/g, 's').replace(/ž/g, 'z').replace(/đ/g, 'dj')
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    }
    initSticky() {
      const bar = $('[data-sticky-atc]', this);
      const source = $('[data-sticky-source]', this);
      if (!bar || !source || !('IntersectionObserver' in window)) return;
      const footer = $('.footer');
      let sourceVisible = true;
      let footerVisible = false;
      const update = () => {
        const show = !sourceVisible && !footerVisible && source.getBoundingClientRect().top < 0;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', String(!show));
        $$('button', bar).forEach((b) => { b.tabIndex = show ? 0 : -1; });
      };
      new IntersectionObserver(([e]) => { sourceVisible = e.isIntersecting; update(); }).observe(source);
      if (footer) new IntersectionObserver(([e]) => { footerVisible = e.isIntersecting; update(); }).observe(footer);
    }
  }
  customElements.define('product-page', ProductPage);
  customElements.define('variant-picker', class extends HTMLElement {});
  customElements.define('product-form', class extends HTMLElement {});

  /* ---------------- Gallery ---------------- */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-track]', this);
      this.thumbs = $('[data-thumbs]', this);
      this.current = $('[data-gallery-current]', this);
      this.total = $('[data-gallery-total]', this);
      this.track.addEventListener('scroll', debounce(() => this.updateCounter(), 50), { passive: true });
      this.addEventListener('click', (e) => {
        const thumb = e.target.closest('[data-thumb]');
        if (thumb) { this.goTo(thumb.closest('[data-media-id]').dataset.mediaId); return; }
        if (e.target.closest('[data-gallery-prev]')) this.step(-1);
        else if (e.target.closest('[data-gallery-next]')) this.step(1);
      });
      this.updateCounter();
      // Desktop hover zoom
      $$('[data-zoom]', this).forEach((z) => {
        const img = $('img', z);
        if (!img) return;
        z.addEventListener('click', () => {
          if (!window.matchMedia('(min-width: 990px)').matches) return;
          z.classList.toggle('is-zoomed');
          if (!z.classList.contains('is-zoomed')) img.style.transformOrigin = '';
        });
        z.addEventListener('mousemove', (e) => {
          if (!z.classList.contains('is-zoomed')) return;
          const r = z.getBoundingClientRect();
          img.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
        });
        z.addEventListener('mouseleave', () => { z.classList.remove('is-zoomed'); img.style.transformOrigin = ''; });
      });
    }
    visibleItems() { return $$('.gallery__item', this.track).filter((li) => !li.hidden); }
    index() { return Math.round(this.track.scrollLeft / (this.track.clientWidth || 1)); }
    scrollToIndex(i, smooth = true) {
      this.track.scrollTo({ left: i * this.track.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
    }
    step(dir) {
      const count = this.visibleItems().length;
      if (count) this.scrollToIndex((this.index() + dir + count) % count);
    }
    goTo(mediaId) {
      const i = this.visibleItems().findIndex((li) => li.dataset.mediaId === String(mediaId));
      if (i > -1) this.scrollToIndex(i);
    }
    // Keep thumbnails in the same order / visibility as the main images
    syncThumbs() {
      if (!this.thumbs) return;
      $$('.gallery__item', this.track).forEach((li) => {
        const t = $(`.gallery__thumb[data-media-id="${li.dataset.mediaId}"]`, this.thumbs);
        if (!t) return;
        t.hidden = li.hidden;
        this.thumbs.append(t);
      });
    }
    updateCounter() {
      const items = this.visibleItems();
      const i = Math.min(items.length, this.index() + 1);
      if (this.current) {
        this.current.textContent = i;
        this.total.textContent = items.length;
      }
      if (this.thumbs) {
        const activeId = items[i - 1] && items[i - 1].dataset.mediaId;
        $$('[data-thumb]', this.thumbs).forEach((b) => {
          const on = b.closest('[data-media-id]').dataset.mediaId === activeId;
          b.setAttribute('aria-current', on ? 'true' : 'false');
          if (on && this.thumbs.scrollWidth > this.thumbs.clientWidth) {
            const li = b.parentElement;
            this.thumbs.scrollTo({ left: li.offsetLeft - this.thumbs.clientWidth / 2 + li.clientWidth / 2, behavior: 'smooth' });
          }
        });
      }
    }
    filterByColor(key, mediaId) {
      const items = $$('.gallery__item', this.track);
      const tagged = items.filter((li) => li.dataset.color === key);
      if (tagged.length) {
        items.forEach((li) => { li.hidden = !!li.dataset.color && li.dataset.color !== key; });
        // put the variant image first within the visible set
        const first = items.find((li) => li.dataset.mediaId === String(mediaId)) || tagged[0];
        if (first && first !== this.track.firstElementChild) this.track.prepend(first);
      } else {
        items.forEach((li) => { li.hidden = false; });
        if (mediaId) this.showMedia(mediaId);
      }
      this.syncThumbs();
      this.scrollToIndex(0, false);
      if (window.matchMedia('(min-width: 990px)').matches) {
        const top = this.getBoundingClientRect().top + window.scrollY - 100;
        if (window.scrollY > top) window.scrollTo({ top, behavior: 'smooth' });
      }
      this.updateCounter();
    }
    showMedia(mediaId) {
      const li = $(`.gallery__item[data-media-id="${mediaId}"]`, this.track);
      if (!li) return;
      this.track.prepend(li);
      this.syncThumbs();
      this.scrollToIndex(0, false);
      this.updateCounter();
    }
  }
  customElements.define('media-gallery', MediaGallery);

  /* ---------------- Product recommendations ---------------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.children.length) return;
      const load = async () => {
        try {
          const doc = parse(await (await fetch(this.dataset.url)).text());
          const fresh = $('product-recommendations', doc);
          if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
        } catch (_) { /* silent */ }
      };
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting) { io.disconnect(); load(); }
        }, { rootMargin: '600px 0px' });
        io.observe(this);
      } else load();
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ---------------- Predictive search ---------------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = $('[data-search-input]', this);
      this.results = $('[data-results]', this);
      this.input.addEventListener('input', debounce(() => this.search(), 250));
    }
    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) { this.results.innerHTML = ''; return; }
      this.abort?.abort();
      this.abort = new AbortController();
      try {
        const url = `${T.routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product,collection,query&resources[limit]=6&resources[options][fields]=title,product_type,variants.title,tag&section_id=predictive-search`;
        const res = await fetch(url, { signal: this.abort.signal });
        const doc = parse(await res.text());
        const sec = $('.shopify-section', doc) || doc.body;
        this.results.innerHTML = sec.innerHTML;
      } catch (_) { /* aborted */ }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ---------------- Collection / search: filters + load more ---------------- */
  const facetSection = $('[data-facets-section]');
  if (facetSection) {
    const sectionId = facetSection.dataset.facetsSection;
    const grid = () => $('[data-grid]', facetSection);

    const renderFrom = (doc, replaceGrid = true) => {
      const fresh = $('[data-facets-section]', doc);
      if (!fresh) return;
      const newTop = $('[data-facets-top]', fresh);
      const curTop = $('[data-facets-top]', facetSection);
      if (newTop && curTop) {
        // keep the drawer open state
        const openId = $('dialog[open]', curTop)?.id;
        const scroll = $('dialog[open] .drawer__body', curTop)?.scrollTop || 0;
        curTop.replaceWith(newTop);
        if (openId) {
          const d = document.getElementById(openId);
          if (d) { d.showModal(); const b = $('.drawer__body', d); if (b) b.scrollTop = scroll; }
        }
      }
      if (replaceGrid) {
        const g = grid();
        const ng = $('[data-grid]', fresh);
        if (g && ng) g.replaceWith(ng);
      }
      const lm = $('[data-load-more]', facetSection);
      const nlm = $('[data-load-more]', fresh);
      if (lm && nlm) lm.replaceWith(nlm);
    };

    const fetchAndRender = async (url) => {
      grid()?.classList.add('is-loading');
      const u = new URL(url, window.location.origin);
      u.searchParams.set('section_id', sectionId);
      try {
        const doc = parse(await (await fetch(u.toString())).text());
        renderFrom(doc);
        u.searchParams.delete('section_id');
        window.history.replaceState({}, '', u.toString());
      } catch (_) {
        window.location.href = url;
      }
    };

    const onFormChange = debounce(() => {
      const form = $('[data-facet-form]', facetSection);
      if (!form) return;
      const fd = new FormData(form);
      const sort = $('select[name="sort_by"]', facetSection);
      if (sort && !fd.has('sort_by')) fd.append('sort_by', sort.value);
      const params = new URLSearchParams();
      for (const [k, v] of fd.entries()) if (v !== '') params.append(k, v);
      fetchAndRender(`${window.location.pathname}?${params.toString()}`);
    }, 350);

    facetSection.addEventListener('change', (e) => { if (e.target.matches('[data-facet-input]')) onFormChange(); });
    facetSection.addEventListener('input', (e) => { if (e.target.matches('input[type="number"][data-facet-input]')) onFormChange(); });
    facetSection.addEventListener('submit', (e) => { if (e.target.matches('[data-facet-form]')) { e.preventDefault(); onFormChange(); } });
    facetSection.addEventListener('click', async (e) => {
      const link = e.target.closest('[data-facet-link]');
      if (link) { e.preventDefault(); fetchAndRender(link.href); return; }
      const more = e.target.closest('[data-load-more-btn]');
      if (more) {
        e.preventDefault();
        more.setAttribute('aria-busy', 'true');
        const u = new URL(more.href, window.location.origin);
        u.searchParams.set('section_id', sectionId);
        try {
          const doc = parse(await (await fetch(u.toString())).text());
          const ng = $('[data-grid]', doc);
          const g = grid();
          if (ng && g) Array.from(ng.children).forEach((li) => g.appendChild(li));
          const lm = $('[data-load-more]', facetSection);
          const nlm = $('[data-load-more]', doc);
          if (lm && nlm) lm.replaceWith(nlm);
        } catch (_) {
          window.location.href = more.href;
        }
      }
    });
  }
})();
